import {
  ApiError,
  Badge,
  Button,
  Card,
  CaseDescription,
  CaseSteps,
  Check,
  ErrorState,
  Field,
  Loading,
  Modal,
  Note,
  TEST_RESULTS,
  TestResultChip,
  testResultHelp,
  errorMessage,
  formatDateTime,
  useToast,
} from "@corecrm/ui";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useBugs,
  useFinishTestSession,
  useMe,
  useRecordTestRun,
  useSessionRuns,
  useTestCases,
  useTestEnvironment,
  useTestSession,
  useTickTestSteps,
} from "../api/queries";
import type { Project, TestResult, TestRun, TestSession, TestStep } from "../api/types";
import { NOTE_REQUIRED, RECORDABLE, readyAssigned, type Assigned } from "../lib/testing";
import { NotAvailable } from "./bits";
import { BugKey, EnvironmentFacts, testingPath } from "./Testing";

/* The session screen (docs/test-suite.md section 7, run by a client under section 8), in stages:
   1. the sign-in details and the environment's setup checklist, ticked once, with nothing else on
   screen; 2. each test assigned to you that is ready, with its own setup, steps, expected result,
   result buttons and notes; 3. only if the environment has reset steps, the reset checklist once the
   tests are finished; then Complete (or End session early, or Abandon with a note). Everything shown
   is read back from the API, so a reload or another device picks up where you were. */

export function TestSessionScreen({ project, sessionId }: { project: Project; sessionId: string }) {
  const session = useTestSession(sessionId);
  const back: [string, string] = [testingPath(project.id), "Back to testing"];
  if (session.isPending) return <Loading />;
  // Another project's session reads as missing, like any record you can't see.
  if (session.error || session.data.project_id !== project.id)
    return <NotAvailable what="testing session" back={back} />;
  // Keyed by session: nothing typed or latched on one session can show on another.
  return <SessionBody key={session.data.id} project={project} session={session.data} />;
}

function SessionBody({ project, session }: { project: Project; session: TestSession }) {
  const me = useMe().data;
  const toast = useToast();
  const navigate = useNavigate();
  const env = useTestEnvironment(session.environment.id);
  const mine = useTestCases(project.id, {
    state: ["active"],
    mine: true,
    environmentId: session.environment.id,
  });
  const runs = useSessionRuns(project.id, session.id);
  const tick = useTickTestSteps(project.id, session.id);
  const finish = useFinishTestSession(project.id, session.id);
  const inProgress = session.status === "in_progress";
  const [abandoning, setAbandoning] = useState(false);
  const [note, setNote] = useState("");
  // End session early: the tests left stay Not run; Reset (if any) and Complete follow.
  const [confirmEarly, setConfirmEarly] = useState(false);
  const [endedEarly, setEndedEarly] = useState(false);
  // Set once the setup stage has been passed (or never applied) on this screen: an environment
  // refetch that changes the step ids must not bring the stage back over a test on screen.
  const [setupPassed, setSetupPassed] = useState(false);
  // Set when the last setup step is ticked: the first test takes focus when it appears.
  const focusFirst = useRef(false);

  const environment = env.data ?? undefined;
  const failed = env.error ?? mine.error ?? runs.error;
  const loaded = !!environment && !!me && mine.isSuccess && runs.isSuccess && !failed;
  const todo = inProgress && environment && me ? readyAssigned(mine.data ?? [], me.id, environment.id) : [];
  const recorded = runs.data ?? [];
  const setupTicked = new Set(session.setup_ticks.map((t) => t.step_id));
  const resetTicked = new Set(session.reset_ticks.map((t) => t.step_id));
  const setupSteps = environment?.setup_steps ?? [];
  const resetSteps = environment?.reset_steps ?? [];
  const setupNotTicked = setupSteps.filter((s) => !setupTicked.has(s.id)).length;
  const resetLeft = resetSteps.filter((s) => !resetTicked.has(s.id)).length;
  // Stage 1 (UI only: the server takes runs before the setup is ticked): until every setup step is
  // ticked only the sign-in details and the checklist show. A session that already has runs goes
  // straight to the tests, and so does one whose environment has no setup steps.
  const gated = inProgress && loaded && !setupPassed && setupNotTicked > 0 && recorded.length === 0;
  useEffect(() => {
    if (inProgress && loaded && !gated) setSetupPassed(true);
  }, [inProgress, loaded, gated]);
  // Stage 3: the tests are finished when none is left to run (or the tester ended them early).
  const casesDone = inProgress && loaded && !gated && (endedEarly || todo.length === 0);
  const hasReset = resetSteps.length > 0;
  // The focus request is spent once the tests stage has rendered: the first test took focus in its own
  // effect; with no test ready the note takes it.
  const emptyNote = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!inProgress || !loaded || gated) return;
    if (focusFirst.current && !todo.length) emptyNote.current?.focus();
    focusFirst.current = false;
  });

  if (failed)
    return (
      <ErrorState
        error={failed}
        onRetry={() => {
          void env.refetch();
          void mine.refetch();
          void runs.refetch();
        }}
      />
    );
  if (env.isPending || mine.isPending || runs.isPending || !me) return <Loading />;
  if (!environment)
    return <NotAvailable what="testing session" back={[testingPath(project.id), "Back to testing"]} />;

  const onTick = (kind: "setup" | "reset", id: string) =>
    tick.mutate(
      { kind, step_ids: [id] },
      {
        onSuccess: (s) => {
          if (
            kind === "setup" &&
            environment.setup_steps.every((st) => s.setup_ticks.some((t) => t.step_id === st.id))
          )
            focusFirst.current = true;
        },
        onError: (e) => toast(errorMessage(e), "err"),
      },
    );
  const done = (message: string) => () => {
    toast(message);
    navigate(testingPath(project.id), { replace: true });
  };
  const fail = (e: unknown) => toast(errorMessage(e), "err");
  const signIn = (
    <>
      <EnvironmentFacts env={environment} />
      {environment.needs_sign_in && (
        <p className="faint">
          The test sign-in has not been added yet. Ask your project manager if you cannot sign in.
        </p>
      )}
    </>
  );
  const setupList = setupSteps.length > 0 && (
    <Checklist
      label="Setup steps"
      steps={setupSteps}
      ticked={setupTicked}
      disabled={!inProgress || tick.isPending}
      onTick={(id) => onTick("setup", id)}
    />
  );
  return (
    <div className="stack">
      <div className="row between">
        <Link className="btn sm" to={testingPath(project.id)}>
          Back to testing
        </Link>
        <span className="row">
          <Badge kind={inProgress ? "info" : session.status === "completed" ? "ok" : "mute"}>
            {inProgress ? "In progress" : session.status === "completed" ? "Completed" : "Abandoned"}
          </Badge>
          <span className="faint">Started {formatDateTime(session.started_at)}</span>
        </span>
      </div>
      <h2 style={{ margin: 0 }}>{environment.name}</h2>
      {!inProgress && (
        <Note kind="acc">
          This session is finished.
          {session.abandon_note ? ` You stopped it: ${session.abandon_note}` : ""}
        </Note>
      )}

      {!inProgress ? (
        <Card title="Sign in and set up">
          {signIn}
          {setupList || <p className="muted">Nothing to set up in this environment.</p>}
        </Card>
      ) : gated ? (
        <Card title="1. Set up">
          <div className="stack">
            <p className="muted">Sign in, then tick every setup step to start testing.</p>
            {signIn}
            {setupList}
          </div>
        </Card>
      ) : (
        <Card>
          <details open={setupNotTicked > 0 ? true : undefined}>
            <summary>
              {setupNotTicked > 0
                ? `Setup: ${setupNotTicked} ${setupNotTicked === 1 ? "step" : "steps"} not ticked`
                : setupSteps.length
                  ? "Setup complete"
                  : "Sign-in details"}
            </summary>
            <div className="stack" style={{ marginTop: 8 }}>
              {signIn}
              {setupList}
            </div>
          </details>
        </Card>
      )}

      {inProgress && !gated && (
        // After End session early the cards stay mounted but hidden, so what was typed on them (and
        // a record request in flight) survives Keep testing.
        <div className="stack" hidden={endedEarly} style={endedEarly ? { display: "none" } : undefined}>
          <h3>2. Test cases</h3>
          {!todo.length && (
            <div ref={emptyNote} tabIndex={-1}>
              <Note>
                {recorded.length
                  ? "You have recorded every test that was ready for you."
                  : "None of your tests in this environment are ready."}
              </Note>
            </div>
          )}
          {todo.map((a, i) => (
            <CaseRunCard
              key={a.test.id}
              project={project}
              session={session}
              item={a}
              focusHeading={i === 0 && focusFirst.current}
            />
          ))}
        </div>
      )}
      {!gated && recorded.length > 0 && (
        <Card title="Recorded in this session">
          <div className="item-list">
            {recorded.map((r) => (
              <RecordedRun key={r.id} project={project} run={r} />
            ))}
          </div>
        </Card>
      )}

      {hasReset && (!inProgress || casesDone) && (
        <Card title={inProgress ? "3. Reset" : "Reset"}>
          <Checklist
            label="Reset steps"
            steps={resetSteps}
            ticked={resetTicked}
            disabled={!inProgress || tick.isPending}
            onTick={(id) => onTick("reset", id)}
          />
        </Card>
      )}

      {inProgress && (
        <>
          <div className="row" style={{ flexWrap: "wrap" }}>
            {casesDone && (
              <Button
                variant="primary"
                busy={finish.isPending}
                disabled={resetLeft > 0}
                onClick={() =>
                  finish.mutate(undefined, {
                    onSuccess: done("Session completed. Thank you."),
                    onError: fail,
                  })
                }
              >
                Complete session
              </Button>
            )}
            {casesDone && endedEarly && <Button onClick={() => setEndedEarly(false)}>Keep testing</Button>}
            {!casesDone && !gated && <Button onClick={() => setConfirmEarly(true)}>End session early</Button>}
            <Button onClick={() => setAbandoning(true)}>Abandon session</Button>
          </div>
          {casesDone && resetLeft > 0 && (
            <p className="faint">Tick every reset step to complete the session.</p>
          )}
        </>
      )}
      {confirmEarly && (
        <Modal
          title="End the session early?"
          onClose={() => setConfirmEarly(false)}
          footer={
            <>
              <Button onClick={() => setConfirmEarly(false)}>Keep testing</Button>
              <Button
                variant="primary"
                onClick={() => {
                  setConfirmEarly(false);
                  setEndedEarly(true);
                }}
              >
                End session
              </Button>
            </>
          }
        >
          <p>
            {todo.length > 0
              ? `${todo.length} ${todo.length === 1 ? "case hasn't" : "cases haven't"} been tested and ${
                  todo.length === 1 ? "stays" : "stay"
                } Not run.`
              : "The cases not yet tested stay Not run."}{" "}
            End the session now?
          </p>
        </Modal>
      )}
      {abandoning && (
        <Modal
          title="Abandon this session"
          onClose={() => setAbandoning(false)}
          footer={
            <>
              <Button onClick={() => setAbandoning(false)}>Keep testing</Button>
              <Button
                variant="danger"
                busy={finish.isPending}
                disabled={!note.trim()}
                onClick={() =>
                  finish.mutate(note.trim(), { onSuccess: done("Session abandoned."), onError: fail })
                }
              >
                Abandon session
              </Button>
            </>
          }
        >
          <Field label="Why are you stopping?" full>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </Modal>
      )}
    </div>
  );
}

/** A checklist ticked once: a ticked step stays ticked (the team sees who ticked it and when). */
function Checklist({
  label,
  steps,
  ticked,
  disabled,
  onTick,
}: {
  label: string;
  steps: TestStep[];
  ticked: Set<string>;
  disabled: boolean;
  onTick: (id: string) => void;
}) {
  if (!steps.length) return null;
  return (
    <div className="stack" role="group" aria-label={label}>
      {steps.map((s) => (
        <Check
          key={s.id}
          checked={ticked.has(s.id)}
          disabled={disabled || ticked.has(s.id)}
          onChange={() => onTick(s.id)}
        >
          {s.text}
        </Check>
      ))}
      <p className="faint">Tick each step once you have done it. A ticked step cannot be unticked.</p>
    </div>
  );
}

/** One test assigned to you: its own setup, steps, what should happen, result and notes. */
function CaseRunCard({
  project,
  session,
  item,
  focusHeading,
}: {
  project: Project;
  session: TestSession;
  item: Assigned;
  focusHeading?: boolean;
}) {
  const { test, link } = item;
  const toast = useToast();
  const record = useRecordTestRun(project.id, session.id);
  const [result, setResult] = useState<TestResult | null>(null);
  const [notes, setNotes] = useState("");
  const [blockedBy, setBlockedBy] = useState("");
  const [attempted, setAttempted] = useState(false);
  // Set once the run is saved: the card is gone before the lists refresh, so no second click.
  const [recorded, setRecorded] = useState(false);
  // Two clicks can land before React re-renders with the busy state.
  const sending = useRef(false);
  const needsNotes = result !== null && NOTE_REQUIRED.includes(result);
  const missingNotes = needsNotes && !notes.trim();
  const logsBug = project.can_log_bugs && project.takes_bugs;
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!focusHeading || !heading.current) return;
    heading.current.focus();
    heading.current.scrollIntoView?.({ block: "start" });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (recorded) return null;
  const submit = () => {
    if (!result || sending.current) return;
    if (missingNotes) {
      setAttempted(true);
      return;
    }
    sending.current = true;
    record.mutate(
      {
        case_id: test.id,
        result,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(result === "blocked" && blockedBy ? { blocking_bug_id: blockedBy } : {}),
      },
      {
        onSuccess: (out) => {
          setRecorded(true);
          toast(
            out.bug
              ? `${test.code} recorded. Bug ${out.bug.key} logged.`
              : result === "fail"
                ? `${test.code} recorded. ${project.takes_bugs ? FAIL_TEAM_TOLD : FAIL_NO_BUG}`
                : `${test.code} recorded.`,
          );
        },
        onError: (e) => {
          sending.current = false;
          toast(
            e instanceof ApiError && e.status === 409 && /already recorded/i.test(errorMessage(e))
              ? "This test was already recorded in this session."
              : errorMessage(e),
            "err",
          );
        },
      },
    );
  };
  return (
    <Card
      title={
        <h2 ref={heading} tabIndex={-1}>
          {`${test.code} · ${test.title}`}
        </h2>
      }
      action={<TestResultChip result={link.current_result} />}
    >
      <div className="stack">
        <CaseDescription description={test.description} />
        {test.setup_steps.length > 0 && (
          <div className="stack" role="group" aria-label={`Set up for ${test.code}`}>
            <strong>Set up for this test</strong>
            <CaseSteps
              key={`${test.id}:setup`}
              checkable
              note={!(test.format === "checklist" && test.checklist_steps)}
              namePrefix="Setup step"
              steps={test.setup_steps}
              label="Set up for this test"
            />
          </div>
        )}
        {test.format === "checklist" && test.checklist_steps ? (
          <div>
            <strong>Steps</strong>
            <CaseSteps
              key={test.id}
              checkable
              steps={test.checklist_steps}
              label="Steps"
              expectedLabel="What should happen"
            />
          </div>
        ) : (
          <>
            <div>
              <strong>Steps</strong>
              <p className="task-desc" style={{ whiteSpace: "pre-wrap" }}>
                {test.steps}
              </p>
            </div>
            <div>
              <strong>What should happen</strong>
              <p className="task-desc" style={{ whiteSpace: "pre-wrap" }}>
                {test.expected_result}
              </p>
            </div>
          </>
        )}
        <div className="row" style={{ flexWrap: "wrap" }} role="group" aria-label={`Result for ${test.code}`}>
          {RECORDABLE.map((r) => (
            <Button
              key={r}
              variant={result === r ? "primary" : undefined}
              aria-pressed={result === r}
              title={testResultHelp(r)}
              onClick={() => {
                setResult(r);
                if (r !== "blocked") setBlockedBy("");
              }}
            >
              {TEST_RESULTS[r].label}
            </Button>
          ))}
        </div>
        <p className="rhint" data-testid="result-hint" aria-live="polite">
          {result
            ? `${TEST_RESULTS[result].label}: ${testResultHelp(result)}`
            : "Pick a result to see what it means."}
        </p>
        {result === "blocked" && project.can_view_bugs && (
          <BlockedByPicker project={project} value={blockedBy} onChange={setBlockedBy} />
        )}
        <Field
          label={needsNotes ? "Notes (required)" : "Notes"}
          hint={
            result === "fail"
              ? logsBug
                ? "Say what happened instead. A bug is logged for the team."
                : project.takes_bugs
                  ? "Say what happened. Your project team will see this result and follow up."
                  : "Say what happened. Your project team will see this result in their test list."
              : undefined
          }
          error={attempted && missingNotes ? "Add a note to record this result." : undefined}
          full
        >
          <textarea
            rows={3}
            aria-required={needsNotes || undefined}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        <div className="row">
          <Button variant="primary" busy={record.isPending} disabled={!result} onClick={submit}>
            Record result
          </Button>
        </div>
      </div>
    </Card>
  );
}

const FAIL_TEAM_TOLD = "Your project team will follow up.";
const FAIL_NO_BUG = "Your project team will see this result in their test list.";

/** Blocked by an existing bug (optional): the open bugs on this project the client can see. */
function BlockedByPicker({
  project,
  value,
  onChange,
}: {
  project: Project;
  value: string;
  onChange: (id: string) => void;
}) {
  const bugs = useBugs(project.id, true);
  return (
    <Field label="Blocked by bug" hint="Optional: a bug that stops you running this test" full>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">None</option>
        {(bugs.data ?? []).map((b) => (
          <option key={b.id} value={b.id}>
            {b.key} · {b.title}
          </option>
        ))}
      </select>
    </Field>
  );
}

function RecordedRun({ project, run }: { project: Project; run: TestRun }) {
  return (
    <div className="item">
      <div className="what">
        <strong>
          <span className="mono faint">{run.case.code}</span> {run.case.title}
        </strong>
        {run.notes && <span>{run.notes}</span>}
        {run.bug && (
          <span>
            Bug logged: <BugKey project={project} bug={run.bug} />
          </span>
        )}
      </div>
      <TestResultChip result={run.result} />
    </div>
  );
}
